import { until } from "@vueuse/core";
import { watch } from "vue";
import { Node } from "prosemirror-model";
import { type BusinessEntity } from "@/stores/business-entities-store";
import { BaseLinkedEntitiesManager } from "../mystekz-linked-entities/manager";
import { storeToRefs } from "pinia";
import { useBusinessEntitiesStore } from "@/stores/business-entities-store";
import { BusinessEntityEntity } from ".";

/**
 * Manages the business entities and replaces
 */
export class BusinessEntitiesManager extends BaseLinkedEntitiesManager<BusinessEntityEntity> {
    constructor() {
        const { entities, isLoading } = storeToRefs(useBusinessEntitiesStore());

        super("");
        this.events = new EventTarget();
        this.ready = until(isLoading).not.toBeTruthy();

        watch(
            entities,
            async (newValue) => {
                if (newValue) {
                    this.setEntities(newValue);
                }
            },
            { immediate: true, deep: true }
        );
    }

    get _hrefFormat() {
        return /stekz-entity:(?<id>[A-Za-z0-9_-]+)/;
    }

    get _textFormat() {
        return /domain-object:(?<name>.*)/;
    }

    itemToEntity(businessEntity: BusinessEntity) {
        return {
            name: businessEntity.name,
            id: businessEntity.identity,
            type: "domain-object",
            data: businessEntity,
            state: "ready",
            href: "",
        } as BusinessEntityEntity;
    }

    check(href: string, text: string) {
        const match = this._hrefFormat.exec(href);
        if (match && this._textFormat.exec(text)) {
            return match;
        } else {
            return false;
        }
    }

    extract(href: string, text: string) {
        const id = this._hrefFormat.exec(href)?.groups?.["id"];
        const name = this._textFormat.exec(text)?.groups?.["name"];
        return (
            this.findById(id || "") ||
            ({
                type: "domain-object",
                state: "pending",
                name: name,
                id: id,
            } as BusinessEntityEntity)
        );
    }

    serialize(entityId: string, node: Node) {
        const entity = this.findById(entityId);
        if (entity) {
            return `[domain-object:${entity.name}](stekz-entity:${entityId})`;
        } else {
            return `[domain-object:${node.attrs.name}](stekz-entity:${entityId})`;
        }
    }
}
